// Zentraler Alarm-Audio-Controller. EIN Controller, dessen Zustand aus
// openAwaitingMerchantOrders.length abgeleitet wird (nie ein Loop pro
// Order). Browser blockieren Autoplay => Audio muss vom Händler einmal
// per Nutzergeste freigegeben werden (enable()).
export class AlarmController {
  private ctx: AudioContext | null = null;
  private enabled = false;
  private ringing = false;
  private timer: number | null = null;

  isEnabled(): boolean {
    return this.enabled;
  }

  /** Nach Nutzergeste aufrufen ("Ton aktivieren"). Gibt Erfolg zurück. */
  async enable(): Promise<boolean> {
    try {
      if (!this.ctx) {
        const Ctor: typeof AudioContext =
          window.AudioContext ?? (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
        if (!Ctor) return false;
        this.ctx = new Ctor();
      }
      if (this.ctx.state === "suspended") await this.ctx.resume();
      this.enabled = true;
      return true;
    } catch {
      this.enabled = false;
      return false;
    }
  }

  /** Alarmzustand setzen: active=true => wiederholender Ton, false => Stille. */
  setActive(active: boolean): void {
    if (active && this.enabled) this.start();
    else this.stop();
  }

  /** Einzelner deutlicher Impuls (z. B. neue Order / Reminder). */
  pulse(): void {
    if (this.enabled) this.beep(880, 0.25);
  }

  private start(): void {
    if (this.ringing) return;
    this.ringing = true;
    const loop = () => {
      if (!this.ringing) return;
      this.beep(880, 0.35);
      this.timer = window.setTimeout(loop, 1500);
    };
    loop();
  }

  private stop(): void {
    this.ringing = false;
    if (this.timer !== null) {
      clearTimeout(this.timer);
      this.timer = null;
    }
  }

  private beep(freq: number, durationSec: number): void {
    if (!this.ctx || !this.enabled) return;
    try {
      const osc = this.ctx.createOscillator();
      const gain = this.ctx.createGain();
      osc.type = "square";
      osc.frequency.value = freq;
      gain.gain.setValueAtTime(0.0001, this.ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.2, this.ctx.currentTime + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, this.ctx.currentTime + durationSec);
      osc.connect(gain).connect(this.ctx.destination);
      osc.start();
      osc.stop(this.ctx.currentTime + durationSec);
    } catch {
      // Audio nicht verfügbar -> visueller Alarm bleibt bestehen.
    }
  }

  /** Beim Logout/Unmount vollständig aufräumen. */
  dispose(): void {
    this.stop();
    this.enabled = false;
    if (this.ctx) {
      try { void this.ctx.close(); } catch { /* ignore */ }
      this.ctx = null;
    }
  }
}
